/**
 * Firestore operations for payment tracking
 */

import { 
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  writeBatch,
  orderBy,
  increment,
  QueryDocumentSnapshot,
  DocumentData,
  DocumentReference,
} from '@firebase/firestore';
import { db } from '../firebase';
import { 
  PaymentReceipt, 
  PaymentReceiptJob, 
  UnpaidWerkerSummary,
  UnpaidJobData,
  WerkerPaymentHistory,
  PaymentDateRange,
} from '../types/payment';
import { calculateJobPay, getCurrentTimestamp } from '../utils/paymentCalculations';
import { FB_COLLECTION_PATH, WORKFLOW_WERKER_TYPES } from './postActionTypes';

/**
 * Get all unpaid jobs for a scheduler, grouped by werker
 */
export async function getUnpaidWerkersForScheduler(schedulerUid: string): Promise<UnpaidWerkerSummary[]> {
  try {
    // Validate input
    if (!schedulerUid || typeof schedulerUid !== 'string') {
      throw new Error('Invalid schedulerUid: must be a non-empty string');
    }

    // Query for all past unpaid jobs where this user is the scheduler
    const unpaidJobsQuery = query(
      collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
      where('docStamp.userInfoFrom.uid', '==', schedulerUid),
      where('jobInfo.status.jobStatusWerker', '==', WORKFLOW_WERKER_TYPES.Past),
      where('jobInfo.status.isPaidWerker', '==', false)
    );

    const snapshot = await getDocs(unpaidJobsQuery);
    
    // Group jobs by werker UID
    const werkerJobsMap = new Map<string, {
      werkerName: string;
      werkerProfilePic: string;
      jobs: UnpaidJobData[];
    }>();

    snapshot.forEach((docSnap: QueryDocumentSnapshot<DocumentData>) => {
      const data = docSnap.data();
      const werkerUid = data.docStamp.userInfoTo.uid;
      
      // Skip if this is the scheduler's own job doc
      if (werkerUid === schedulerUid) return;

      // Skip jobs where the scheduler chose to waive payment at cancellation time
      if (data.jobInfo?.status?.paymentWaivedByScheduler === true) return;

      const werkerFirstName = data.docStamp.userInfoTo.firstName || '';
      const werkerLastName = data.docStamp.userInfoTo.lastName || '';
      const werkerName = `${werkerFirstName} ${werkerLastName}`.trim();
      const werkerProfilePic = data.docStamp.userInfoTo.profilePicURL || '';

      // Calculate estimated payment for this job
      const calculatedPay = calculateJobPay(
        data.jobInfo.core.pay,
        data.jobInfo.core.dateTimeStart,
        data.jobInfo.core.dateTimeFinish,
        data.jobInfo.core.shiftNotes
      );

      const jobData: UnpaidJobData = {
        jobDocId: docSnap.id,
        jobDocPath: docSnap.ref.path,
        uniqueJobId: data.jobInfo.core.uniqueJobId,
        company: data.jobInfo.core.company,
        dateTimeStart: data.jobInfo.core.dateTimeStart,
        dateTimeFinish: data.jobInfo.core.dateTimeFinish,
        pay: data.jobInfo.core.pay,
        notes: data.jobInfo.core.shiftNotes,
        estimatedAmount: calculatedPay.amount,
        selected: false,
        isCancelled: data.jobInfo?.status?.jobCancelledByScheduler === true,
      };

      if (!werkerJobsMap.has(werkerUid)) {
        werkerJobsMap.set(werkerUid, {
          werkerName,
          werkerProfilePic,
          jobs: [],
        });
      }

      werkerJobsMap.get(werkerUid)!.jobs.push(jobData);
    });

    // Convert map to array of summaries
    const summaries: UnpaidWerkerSummary[] = [];
    werkerJobsMap.forEach((value, werkerUid) => {
      const totalUnpaid = value.jobs.reduce((sum, job) => sum + job.estimatedAmount, 0);
      summaries.push({
        werkerUid,
        werkerName: value.werkerName,
        werkerProfilePic: value.werkerProfilePic,
        unpaidJobs: value.jobs,
        totalUnpaid,
        jobCount: value.jobs.length,
      });
    });

    // Sort by total unpaid (descending)
    summaries.sort((a, b) => b.totalUnpaid - a.totalUnpaid);

    return summaries;
  } catch (error) {
    console.error('Error fetching unpaid werkers:', error);
    throw error;
  }
}

/**
 * Create a payment receipt and return the receipt ID
 */
export async function createPaymentReceipt(
  schedulerUid: string,
  schedulerName: string,
  werkerUid: string,
  werkerName: string,
  jobs: PaymentReceiptJob[],
  totalAmount: number,
  notes?: string
): Promise<string> {
  try {
    // Validate inputs
    if (!schedulerUid || typeof schedulerUid !== 'string') {
      throw new Error('Invalid schedulerUid: must be a non-empty string');
    }
    if (!werkerUid || typeof werkerUid !== 'string') {
      throw new Error('Invalid werkerUid: must be a non-empty string');
    }

    const receiptsCollection = collection(db, `users/${schedulerUid}/paymentReceipts`);
    const newReceiptRef = doc(receiptsCollection);
    const receiptId = newReceiptRef.id;

    const now = getCurrentTimestamp();

    const receipt: PaymentReceipt = {
      receiptId,
      schedulerUid,
      werkerUid,
      schedulerName,
      werkerName,
      paymentDate: now,
      jobs,
      totalAmount,
      notes: notes || '',
      createdAt: now,
      updatedAt: now,
      werkerIsRead: false,
      werkerReadAt: null,
    };

    await setDoc(newReceiptRef, receipt);

    // Increment the werker's unread payment receipts badge counter
    const paymentReceiptsCountRef = doc(db, `users/${werkerUid}/users_PaymentReceiptsCount`, werkerUid);
    await setDoc(
      paymentReceiptsCountRef,
      { paymentReceiptsUnreadCount: increment(1), uid: werkerUid },
      { merge: true }
    );

    return receiptId;
  } catch (error) {
    console.error('Error creating payment receipt:', error);
    throw error;
  }
}

/**
 * ATOMIC: Mark jobs paid AND create payment receipt in a single batch commit.
 * Either both succeed or neither does — prevents the job being marked paid
 * without a receipt, or a receipt existing for an "unpaid" job.
 *
 * For large numbers of jobs (>~445) the writes are split across multiple
 * sequential batches. Each batch is still atomic within itself; in the
 * rare edge case of a partial failure mid-way through chunks the caller
 * receives the error and should present a retry surface to the user.
 */
export async function markPaidAndCreateReceipt(
  schedulerUid: string,
  schedulerName: string,
  werkerUid: string,
  werkerName: string,
  jobs: PaymentReceiptJob[],
  jobDocPaths: string[],
  totalAmount: number,
  notes?: string
): Promise<string> {
  // Validate inputs
  if (!schedulerUid || typeof schedulerUid !== 'string') {
    throw new Error('Invalid schedulerUid');
  }
  if (!werkerUid || typeof werkerUid !== 'string') {
    throw new Error('Invalid werkerUid');
  }
  const invalidPaths = jobDocPaths.filter(p => !p || typeof p !== 'string');
  if (invalidPaths.length > 0) {
    throw new Error(`Invalid document paths: ${invalidPaths.length} empty/invalid paths`);
  }

  // ── Pre-query: find scheduler job docs that become fully paid by this payment ──
  // Done before the batch so we can include the isPaidJob update atomically.
  // Wrapped in try/catch — a query failure never blocks the payment itself.
  const fullyPaidSchedulerRefs: DocumentReference<DocumentData>[] = [];
  try {
    const paidPathsSet = new Set(jobDocPaths);
    const uniqueJobIds = Array.from(new Set(jobs.map(j => j.uniqueJobId)));

    await Promise.all(uniqueJobIds.map(async (uniqueJobId) => {
      const q = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
        where('docStamp.userInfoFrom.uid', '==', schedulerUid),
        where('jobInfo.core.uniqueJobId', '==', uniqueJobId)
      );
      const snap = await getDocs(q);

      let schedulerDocRef: DocumentReference<DocumentData> | null = null;
      let totalActiveWerkers = 0;
      let paidCount = 0;

      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const toUid = data.docStamp?.userInfoTo?.uid;

        if (toUid === schedulerUid) {
          // Scheduler's own copy
          schedulerDocRef = docSnap.ref as DocumentReference<DocumentData>;
        } else {
          // Werker doc — count active werkers (Past or Scheduled)
          const status = data.jobInfo?.status?.jobStatusWerker;
          if (
            status === WORKFLOW_WERKER_TYPES.Past ||
            status === WORKFLOW_WERKER_TYPES.Scheduled
          ) {
            totalActiveWerkers++;
            const alreadyPaid = data.jobInfo?.status?.isPaidWerker === true;
            const beingPaidNow = paidPathsSet.has(docSnap.ref.path);
            if (alreadyPaid || beingPaidNow) paidCount++;
          }
        }
      });

      if (schedulerDocRef && totalActiveWerkers > 0 && paidCount >= totalActiveWerkers) {
        fullyPaidSchedulerRefs.push(schedulerDocRef);
      }
    }));
  } catch (err) {
    // Best-effort — isPaidWerkers can be reconciled later if this query fails
    console.error('[markPaidAndCreateReceipt] pre-query failed — check Firestore rules/indexes:', err);
  }

  // Pre-generate the receipt ID so we can include it in the same batch
  const receiptsCollection = collection(db, `users/${schedulerUid}/paymentReceipts`);
  const newReceiptRef = doc(receiptsCollection);
  const receiptId = newReceiptRef.id;
  const now = getCurrentTimestamp();

  const receipt: PaymentReceipt = {
    receiptId,
    schedulerUid,
    werkerUid,
    schedulerName,
    werkerName,
    paymentDate: now,
    jobs,
    totalAmount,
    notes: notes || '',
    createdAt: now,
    updatedAt: now,
    werkerIsRead: false,
    werkerReadAt: null,
  };

  // Badge counter ref (merged setDoc — supports increment)
  const badgeCountRef = doc(db, `users/${werkerUid}/users_PaymentReceiptsCount`, werkerUid);

  // We need to fit everything into batches of ≤ 450 writes.
  // The receipt doc + badge counter = 2 fixed writes; put them in the first batch.
  const MAX_PER_BATCH = 448; // 450 minus the 2 fixed writes in the first batch
  const MAX_SUBSEQUENT = 450;

  // First batch: receipt + badge + first MAX_PER_BATCH job docs
  let batch = writeBatch(db);
  batch.set(newReceiptRef, receipt);

  const firstChunkPaths = jobDocPaths.slice(0, MAX_PER_BATCH);
  for (const jobDocPath of firstChunkPaths) {
    batch.update(doc(db, jobDocPath), {
      'jobInfo.status.isPaidWerker': true,
      'docStamp.timestamp.updatedAt': now,
    });
  }

  // Mark scheduler job docs as fully paid (atomic with isPaidWerker updates above)
  for (const schedulerDocRef of fullyPaidSchedulerRefs) {
    batch.update(schedulerDocRef, {
      'jobInfo.status.isPaidWerkers': true,
      'docStamp.timestamp.updatedAt': now,
    });
  }

  await batch.commit();

  // Remaining job paths (very rare — only for payments with >448 jobs)
  const remainingPaths = jobDocPaths.slice(MAX_PER_BATCH);
  for (let i = 0; i < remainingPaths.length; i += MAX_SUBSEQUENT) {
    const chunk = remainingPaths.slice(i, i + MAX_SUBSEQUENT);
    const extraBatch = writeBatch(db);
    for (const jobDocPath of chunk) {
      extraBatch.update(doc(db, jobDocPath), {
        'jobInfo.status.isPaidWerker': true,
        'docStamp.timestamp.updatedAt': now,
      });
    }
    await extraBatch.commit();
  }

  // Increment the werker's unread badge counter (best-effort, non-critical)
  try {
    await setDoc(badgeCountRef, { paymentReceiptsUnreadCount: increment(1), uid: werkerUid }, { merge: true });
  } catch {
    // Badge update is cosmetic — don't fail the payment over it
  }

  return receiptId;
}

/**
 * Batch update isPaidWerker status for multiple job documents
 * Uses chunked batches to stay under Firestore's 500-operation limit
 */
export async function updateJobsPaidStatus(jobDocPaths: string[]): Promise<void> {
  try {
    // Validate all paths are non-empty
    const invalidPaths = jobDocPaths.filter(path => !path || typeof path !== 'string');
    if (invalidPaths.length > 0) {
      throw new Error(`Invalid document paths detected: ${invalidPaths.length} empty/invalid paths`);
    }

    const MAX_WRITES_PER_BATCH = 450; // Leave headroom under 500 limit
    let batch = writeBatch(db);
    let writesInBatch = 0;

    const commitBatchIfNeeded = async (force = false) => {
      if (writesInBatch === 0) return;
      if (!force && writesInBatch < MAX_WRITES_PER_BATCH) return;

      await batch.commit();
      batch = writeBatch(db);
      writesInBatch = 0;
    };

    for (const jobDocPath of jobDocPaths) {
      // Use the document path to create a direct reference
      const docRef = doc(db, jobDocPath);
      
      batch.update(docRef, {
        'jobInfo.status.isPaidWerker': true,
        'docStamp.timestamp.updatedAt': getCurrentTimestamp(),
      });
      writesInBatch += 1;

      await commitBatchIfNeeded(false);
    }

    // Commit any remaining writes
    await commitBatchIfNeeded(true);
  } catch (error) {
    console.error('Error updating job paid status:', error);
    throw error;
  }
}

/**
 * Get payment receipts for a scheduler within a date range
 * Returns aggregated data grouped by werker
 */
export async function getPaymentReceipts(
  schedulerUid: string,
  dateRange?: PaymentDateRange
): Promise<WerkerPaymentHistory[]> {
  try {
    // Validate input
    if (!schedulerUid || typeof schedulerUid !== 'string') {
      throw new Error('Invalid schedulerUid: must be a non-empty string');
    }

    const receiptsCollection = collection(db, `users/${schedulerUid}/paymentReceipts`);
    let receiptsQuery = query(receiptsCollection, orderBy('paymentDate', 'desc'));

    const snapshot = await getDocs(receiptsQuery);
    
    // Filter by date range if provided
    const startDate = dateRange?.startDate || 0;
    const endDate = dateRange?.endDate || Number.MAX_SAFE_INTEGER;

    const receipts: PaymentReceipt[] = [];
    snapshot.forEach((docSnap: QueryDocumentSnapshot<DocumentData>) => {
      const data = docSnap.data() as PaymentReceipt;
      if (data.paymentDate >= startDate && data.paymentDate <= endDate) {
        receipts.push(data);
      }
    });

    // Group by werker
    const werkerHistoryMap = new Map<string, {
      werkerName: string;
      werkerProfilePic: string;
      receipts: PaymentReceipt[];
    }>();

    receipts.forEach((receipt) => {
      if (!werkerHistoryMap.has(receipt.werkerUid)) {
        werkerHistoryMap.set(receipt.werkerUid, {
          werkerName: receipt.werkerName,
          werkerProfilePic: '', // Would need to fetch from user profile or store in receipt
          receipts: [],
        });
      }

      werkerHistoryMap.get(receipt.werkerUid)!.receipts.push(receipt);
    });

    // Convert to array
    const historyArray: WerkerPaymentHistory[] = [];
    werkerHistoryMap.forEach((value, werkerUid) => {
      const totalPaid = value.receipts.reduce((sum, r) => sum + r.totalAmount, 0);
      historyArray.push({
        werkerUid,
        werkerName: value.werkerName,
        werkerProfilePic: value.werkerProfilePic,
        receipts: value.receipts,
        totalPaid,
        receiptCount: value.receipts.length,
      });
    });

    // Sort by total paid (descending)
    historyArray.sort((a, b) => b.totalPaid - a.totalPaid);

    return historyArray;
  } catch (error) {
    console.error('Error fetching payment receipts:', error);
    throw error;
  }
}

/**
 * Update the notes field of a payment receipt
 */
export async function updateReceiptNotes(
  schedulerUid: string,
  receiptId: string,
  notes: string
): Promise<void> {
  try {
    // Validate inputs
    if (!schedulerUid || typeof schedulerUid !== 'string') {
      throw new Error('Invalid schedulerUid: must be a non-empty string');
    }
    if (!receiptId || typeof receiptId !== 'string') {
      throw new Error('Invalid receiptId: must be a non-empty string');
    }

    const receiptRef = doc(db, `users/${schedulerUid}/paymentReceipts/${receiptId}`);
    await updateDoc(receiptRef, {
      notes,
      updatedAt: getCurrentTimestamp(),
    });
  } catch (error) {
    console.error('Error updating receipt notes:', error);
    throw error;
  }
}

/**
 * Get a single payment receipt by ID
 */
export async function getPaymentReceiptById(
  schedulerUid: string,
  receiptId: string
): Promise<PaymentReceipt | null> {
  try {
    // Validate inputs
    if (!schedulerUid || typeof schedulerUid !== 'string') {
      throw new Error('Invalid schedulerUid: must be a non-empty string');
    }
    if (!receiptId || typeof receiptId !== 'string') {
      throw new Error('Invalid receiptId: must be a non-empty string');
    }

    const receiptRef = doc(db, `users/${schedulerUid}/paymentReceipts/${receiptId}`);
    const snapshot = await getDoc(receiptRef);
    
    if (!snapshot.exists()) return null;
    
    return snapshot.data() as PaymentReceipt;
  } catch (error) {
    console.error('Error fetching payment receipt:', error);
    return null;
  }
}

/**
 * Find the receipt id (if any) that includes a payment for a given werker on a given job.
 * Matches first by uniqueJobId; falls back to werker job doc id for legacy receipts.
 * Returns null when no matching receipt exists.
 */
export async function findPaymentReceiptForJob(
  schedulerUid: string,
  werkerUid: string,
  uniqueJobId: string,
  werkerJobDocId?: string
): Promise<string | null> {
  try {
    if (!schedulerUid || !werkerUid || !uniqueJobId) return null;

    const receiptsCollection = collection(db, `users/${schedulerUid}/paymentReceipts`);
    const q = query(receiptsCollection, where('werkerUid', '==', werkerUid));
    const snapshot = await getDocs(q);

    for (const docSnap of snapshot.docs) {
      const receipt = docSnap.data() as PaymentReceipt;
      const jobs: PaymentReceiptJob[] = Array.isArray(receipt.jobs) ? receipt.jobs : [];
      const match = jobs.some(j =>
        j.uniqueJobId === uniqueJobId ||
        (werkerJobDocId ? j.jobDocId === werkerJobDocId : false)
      );
      if (match) return docSnap.id;
    }
    return null;
  } catch (error) {
    console.error('Error finding payment receipt for job:', error);
    return null;
  }
}
